import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import ViewPrescriptionModal from '../components/ViewPrescriptionModal';
import FilterSelect from '../components/FilterSelect';
import DateInput from '../components/DateInput';
import { toast } from 'react-toastify';
import './UserHealthRecords.css';

const DOC_TYPES = [
  { value: 'LAB', label: 'Lab report' },
  { value: 'PRESCRIPTION', label: 'Prescription' },
  { value: 'SCAN', label: 'Scan / imaging' },
  { value: 'OTHER', label: 'Other' },
  { value: 'SUMMARY', label: 'Health summary' },
];

const docTypeIcon = (type) => {
  const map = {
    LAB: 'bi-droplet-half',
    PRESCRIPTION: 'bi-file-medical',
    SCAN: 'bi-images',
    SUMMARY: 'bi-journal-text',
    OTHER: 'bi-file-earmark',
  };
  return map[type] || 'bi-file-earmark';
};

const statusBadgeClass = (status) => {
  const s = (status || '').toUpperCase();
  if (s === 'COMPLETED' || s === 'COMPLETED_SESSION') return 'hr-badge-completed';
  if (s === 'ACCEPTED' || s === 'CONFIRMED') return 'hr-badge-accepted';
  if (s === 'PENDING' || s === 'UPCOMING') return 'hr-badge-pending';
  if (s === 'CANCELLED' || s === 'REJECTED') return 'hr-badge-cancelled';
  return 'badge bg-secondary';
};

function EmptyState({ icon, title, subtitle, action }) {
  return (
    <div className="hr-empty">
      <div className="hr-empty-icon">
        <i className={`bi ${icon}`} />
      </div>
      <h6 className="fw-bold text-dark mb-2">{title}</h6>
      <p className="text-muted small mb-3 mx-auto" style={{ maxWidth: '320px' }}>
        {subtitle}
      </p>
      {action}
    </div>
  );
}

function UserHealthRecords() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('prescriptions');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [personFilter, setPersonFilter] = useState('0');
  const [family, setFamily] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [records, setRecords] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewRx, setViewRx] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    document_type: 'OTHER',
    description: '',
    file: null,
    family_member_id: '0',
  });

  const filterParams = useCallback(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (personFilter && personFilter !== 'all') params.family_member_id = personFilter;
    return params;
  }, [dateFrom, dateTo, personFilter]);

  const loadFamily = useCallback(async () => {
    try {
      const res = await api.get('user/family/');
      setFamily(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterParams();
      const [rxRes, recRes, visitRes] = await Promise.all([
        api.get('prescriptions/user/list/', { params }),
        api.get('prescriptions/user/records/', { params }),
        api.get('prescriptions/user/visits/', { params }),
      ]);
      setPrescriptions(rxRes.data.prescriptions || []);
      setRecords(recRes.data.records || []);
      setVisits(visitRes.data.visits || []);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/user/login');
        return;
      }
      toast.error('Failed to load health records');
    } finally {
      setLoading(false);
    }
  }, [filterParams, navigate]);

  useEffect(() => {
    const init = async () => {
      try {
        const session = await api.get('user/session-check/');
        if (!session.data.authenticated) {
          navigate('/user/login');
          return;
        }
        await loadFamily();
        await loadData();
      } catch {
        navigate('/user/login');
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPrescription = async (rx) => {
    try {
      const res = await api.get(`prescriptions/user/${rx.id}/`);
      setViewRx(res.data);
    } catch {
      setViewRx(rx);
    }
  };

  const openPrescriptionForVisit = async (visit) => {
    try {
      const res = await api.get(`prescriptions/user/appointment/${visit.appointment_id}/`);
      if (res.data.prescription) setViewRx(res.data.prescription);
      else toast.info('No prescription for this visit yet.');
    } catch {
      toast.error('Could not load prescription');
    }
  };

  const mediaUrl = (file) => {
    if (!file) return null;
    if (file.startsWith('http')) return file;
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return file.startsWith('/') ? `${base}${file}` : `${base}/${file}`;
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.title.trim()) {
      toast.warning('Title is required');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', uploadForm.title.trim());
      fd.append('document_type', uploadForm.document_type);
      fd.append('description', uploadForm.description);
      fd.append('family_member_id', uploadForm.family_member_id);
      if (uploadForm.file) fd.append('file', uploadForm.file);
      await api.post('prescriptions/user/documents/upload/', fd);
      toast.success('Document uploaded');
      setShowUpload(false);
      setUploadForm({
        title: '',
        document_type: 'OTHER',
        description: '',
        file: null,
        family_member_id: personFilter,
      });
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteRecord = async (rec) => {
    if (!rec.can_delete || rec.legacy) return;
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`prescriptions/user/documents/${rec.id}/delete/`);
      toast.success('Deleted');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editRecord) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', editRecord.title);
      fd.append('document_type', editRecord.document_type);
      fd.append('description', editRecord.description || '');
      if (editRecord.newFile) fd.append('file', editRecord.newFile);
      await api.patch(`prescriptions/user/documents/${editRecord.id}/`, fd);
      toast.success('Updated');
      setEditRecord(null);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setUploading(false);
    }
  };

  const openUploadModal = () => {
    setUploadForm((f) => ({ ...f, family_member_id: personFilter }));
    setShowUpload(true);
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setPersonFilter('0');
  };

  const formatDocType = (type) => {
    const found = DOC_TYPES.find((t) => t.value === type);
    return found ? found.label : type;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted small mt-3 mb-0">Loading your records…</p>
        </div>
      );
    }

    if (activeTab === 'prescriptions') {
      if (prescriptions.length === 0) {
        return (
          <EmptyState
            icon="bi-file-medical"
            title="No prescriptions yet"
            subtitle="Prescriptions from completed visits will appear here after your doctor sends them."
          />
        );
      }
      return prescriptions.map((rx) => (
        <div key={rx.id} className="hr-item-card rx-border">
          <div className="d-flex align-items-start gap-3">
            <div className="hr-item-avatar rx">
              <i className="bi bi-capsule" />
            </div>
            <div className="flex-grow-1 min-w-0">
              <h6 className="fw-bold mb-1 text-dark"> {rx.doctor_name}</h6>
              <p className="text-muted small mb-2 mb-md-1">
                <i className="bi bi-person me-1" />
                {rx.patient_name}
                {rx.patient_relation ? ` · ${rx.patient_relation}` : ''}
              </p>
              <span className="hr-tag">
                <i className="bi bi-calendar3 me-1" />
                Visit {rx.visit_date || '—'}
              </span>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-primary rounded-pill hr-btn-action flex-shrink-0"
              onClick={() => openPrescription(rx)}
            >
              <i className="bi bi-eye me-1" />
              View
            </button>
          </div>
        </div>
      ));
    }

    if (activeTab === 'visits') {
      if (visits.length === 0) {
        return (
          <EmptyState
            icon="bi-calendar-check"
            title="No visits in this range"
            subtitle="Try adjusting the date filter or book a new appointment from the dashboard."
            action={
              <button
                type="button"
                className="btn btn-primary rounded-pill btn-sm"
                onClick={() => navigate('/user/bookings')}
              >
                My bookings
              </button>
            }
          />
        );
      }
      return visits.map((v) => (
        <div key={v.appointment_id} className="hr-item-card visit-border">
          <div className="d-flex align-items-start gap-3 flex-wrap">
            <div className="hr-item-avatar visit">
              <i className="bi bi-camera-video" />
            </div>
            <div className="flex-grow-1 min-w-0">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <h6 className="fw-bold mb-0 text-dark">{v.doctor_name}</h6>
                <span className={`badge rounded-pill ${statusBadgeClass(v.status)}`}>
                  {v.status}
                </span>
              </div>
              <p className="text-muted small mb-1">
                <i className="bi bi-person me-1" />
                {v.patient_name}
                {v.family_member_relation ? ` · ${v.family_member_relation}` : ''}
              </p>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span className="hr-tag">
                  <i className="bi bi-clock me-1" />
                  {v.date} · {v.start_time}
                </span>
              </div>

              {/* Booking Symptoms Notes */}
              {v.patient_notes && (
                <div className="p-2 px-3 bg-light rounded-3 border-start border-primary border-3 mb-2 small text-dark">
                  <span className="fw-bold text-primary d-block mb-1">
                    <i className="bi bi-chat-left-text-fill me-1"></i> Symptoms / Illness Notes:
                  </span>
                  <span className="text-secondary">{v.patient_notes}</span>
                </div>
              )}

              {/* Attached Booking Medical Report(s) */}
              {(v.medical_reports?.length
                ? v.medical_reports
                : (v.medical_report ? [v.medical_report] : [])
              ).map((reportUrl, idx) => (
                <div className="mt-2" key={`${reportUrl}-${idx}`}>
                  <a
                    href={mediaUrl(reportUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary rounded-pill text-decoration-none px-3 py-1 fw-semibold d-inline-flex align-items-center"
                  >
                    <i className="bi bi-file-earmark-medical-fill me-1 text-primary"></i>
                    {v.medical_reports?.length > 1
                      ? `Open Medical Report ${idx + 1}`
                      : 'Open Booking Medical Report'}
                    <i className="bi bi-box-arrow-up-right ms-2 small"></i>
                  </a>
                </div>
              ))}
            </div>
            {v.has_prescription && (
              <button
                type="button"
                className="btn btn-sm btn-success rounded-pill hr-btn-action flex-shrink-0"
                onClick={() => openPrescriptionForVisit(v)}
              >
                <i className="bi bi-file-medical me-1" />
                Rx
              </button>
            )}
          </div>
        </div>
      ));
    }

    if (records.length === 0) {
      return (
        <EmptyState
          icon="bi-folder2-open"
          title="No documents yet"
          subtitle="Upload lab reports, scans, or other health files to keep everything in one place."
          action={
            <button type="button" className="btn btn-primary rounded-pill btn-sm" onClick={openUploadModal}>
              <i className="bi bi-cloud-upload me-1" />
              Upload document
            </button>
          }
        />
      );
    }

    return records.map((rec) => (
      <div key={rec.id} className="hr-item-card doc-border">
        <div className="hr-item-main">
          <div className="hr-item-avatar doc">
            <i className={`bi ${docTypeIcon(rec.document_type)}`} />
          </div>
          <div className="hr-item-body">
            <h6 className="fw-bold mb-1 text-dark">{rec.title}</h6>
            <p className="text-muted small mb-2">
              <i className="bi bi-person me-1" />
              {rec.person_name}
              {rec.person_relation ? ` · ${rec.person_relation}` : ''}
            </p>
            <div className="d-flex flex-wrap gap-1">
              <span className="hr-tag">{formatDocType(rec.document_type)}</span>
              {rec.legacy && <span className="hr-tag">Legacy</span>}
              {rec.uploaded_at && (
                <span className="hr-tag">
                  <i className="bi bi-calendar3 me-1" />
                  {new Date(rec.uploaded_at).toLocaleDateString()}
                </span>
              )}
            </div>
            {rec.description && (
              <p className="small text-muted mt-2 mb-0">{rec.description}</p>
            )}
          </div>
          <div className="hr-item-actions">
            {rec.file && (
              <a
                href={mediaUrl(rec.file)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary rounded-pill hr-btn-action"
              >
                Open
              </a>
            )}
            {rec.can_edit && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary rounded-pill hr-btn-action"
                onClick={() => setEditRecord({ ...rec, newFile: null })}
              >
                Edit
              </button>
            )}
            {rec.can_delete && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger rounded-pill hr-btn-action"
                onClick={() => handleDeleteRecord(rec)}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    ));
  };

  const tabLabels = {
    prescriptions: 'Prescriptions',
    records: 'Documents',
    visits: 'Consultations & Notes',
  };

  const tabCounts = {
    prescriptions: prescriptions.length,
    records: records.length,
    visits: visits.length,
  };

  return (
    <div className="hr-page pb-5">
      <UserNavbar />
      <div className="container py-4 mt-3">
        {/* Hero */}
        <div className="hr-hero p-4 p-md-5 mb-4 position-relative">
          <div className="row align-items-center position-relative" style={{ zIndex: 1 }}>
            <div className="col-md-8">
              <h2 className="fw-bold mb-2 text-white">
                <i className="bi bi-heart-pulse me-2" />
                Health Records
              </h2>
              <p className="mb-0 opacity-75">
                Prescriptions, documents, and consultation notes — all in one secure hub.
              </p>
            </div>
            <div className="col-md-4 d-flex gap-2 flex-wrap justify-content-md-end mt-3 mt-md-0">
              {activeTab === 'records' && (
                <button
                  type="button"
                  className="btn btn-light btn-sm rounded-pill fw-semibold"
                  onClick={openUploadModal}
                >
                  <i className="bi bi-cloud-upload me-1" />
                  Upload
                </button>
              )}
              <button
                type="button"
                className="btn btn-outline-light btn-sm rounded-pill"
                onClick={() => navigate('/user/family')}
              >
                <i className="bi bi-people me-1" />
                Family
              </button>
            </div>
          </div>
        </div>

        {/* Stat cards */}
        <div className="row g-3 mb-4">
          {[
            ['prescriptions', 'rx', 'bi-capsule', 'Prescriptions'],
            ['records', 'doc', 'bi-folder2-open', 'Documents'],
            ['visits', 'visit', 'bi-calendar-check', 'Consultations & Notes'],
          ].map(([tab, iconClass, icon, label]) => (
            <div className="col-md-4" key={tab}>
              <div
                className={`hr-stat-card ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
                onKeyDown={(e) => e.key === 'Enter' && setActiveTab(tab)}
                role="button"
                tabIndex={0}
              >
                <div className="d-flex align-items-center gap-3">
                  <div className={`hr-stat-icon ${iconClass}`}>
                    <i className={`bi ${icon}`} />
                  </div>
                  <div>
                    <div className="text-muted small fw-semibold text-uppercase" style={{ letterSpacing: '0.04em' }}>
                      {label}
                    </div>
                    <div className="fs-4 fw-bold text-dark">{tabCounts[tab]}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="hr-filter-card p-4 mb-4">
          <div className="d-flex align-items-center gap-2 mb-3">
            <i className="bi bi-funnel text-primary" />
            <span className="fw-bold text-dark">Filters</span>
          </div>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small fw-semibold text-muted mb-1">From</label>
              <DateInput
                value={dateFrom}
                onChange={setDateFrom}
                placeholder="From date"
                aria-label="From date"
                size="sm"
              />
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small fw-semibold text-muted mb-1">To</label>
              <DateInput
                value={dateTo}
                onChange={setDateTo}
                placeholder="To date"
                aria-label="To date"
                size="sm"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label small fw-semibold text-muted mb-1">Person</label>
              <FilterSelect
                value={personFilter}
                onChange={setPersonFilter}
                options={[
                  { value: 'all', label: 'All people' },
                  { value: '0', label: 'Myself' },
                  ...family.map((m) => ({
                    value: String(m.id),
                    label: `${m.name} (${m.relation})`,
                  })),
                ]}
                placeholder="Person"
                aria-label="Person"
                size="sm"
                variant="bordered"
              />
            </div>
            <div className="col-12 col-md-2 d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary btn-sm rounded-pill flex-grow-1 fw-semibold"
                onClick={loadData}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm rounded-pill"
                onClick={clearFilters}
                title="Clear filters"
              >
                <i className="bi bi-arrow-counterclockwise" />
              </button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="hr-content-card">
          <div className="p-3 p-md-4 border-bottom bg-light bg-opacity-50">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <h5 className="fw-bold mb-0 text-dark">{tabLabels[activeTab]}</h5>
              <div className="hr-tabs">
                {Object.keys(tabLabels).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`hr-tab-btn ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tabLabels[tab]} ({tabCounts[tab]})
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="p-3 p-md-4">{renderContent()}</div>
        </div>
      </div>

      {viewRx && <ViewPrescriptionModal prescription={viewRx} onClose={() => setViewRx(null)} />}

      {showUpload && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(15,23,42,0.55)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <form onSubmit={handleUpload}>
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold">
                    <i className="bi bi-cloud-upload text-primary me-2" />
                    Upload document
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowUpload(false)} />
                </div>
                <div className="modal-body pt-3">
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">For</label>
                    <FilterSelect
                      value={uploadForm.family_member_id}
                      onChange={(family_member_id) => setUploadForm({ ...uploadForm, family_member_id })}
                      options={[
                        { value: '0', label: 'Myself' },
                        ...family.map((m) => ({
                          value: String(m.id),
                          label: `${m.name} (${m.relation})`,
                        })),
                      ]}
                      placeholder="Myself"
                      aria-label="For"
                      size="sm"
                      variant="bordered"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Title *</label>
                    <input
                      className="form-control form-control-sm"
                      value={uploadForm.title}
                      onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                      required
                      placeholder="e.g. Blood test report"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Type</label>
                    <FilterSelect
                      value={uploadForm.document_type}
                      onChange={(document_type) => setUploadForm({ ...uploadForm, document_type })}
                      options={DOC_TYPES}
                      placeholder="Type"
                      aria-label="Document type"
                      size="sm"
                      variant="bordered"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Notes</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      value={uploadForm.description}
                      onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                      placeholder="Optional details"
                    />
                  </div>
                  <div className="mb-0">
                    <label className="form-label small fw-semibold">File</label>
                    <div className="hr-upload-zone">
                      <i className="bi bi-file-earmark-arrow-up text-primary fs-4 d-block mb-2" />
                      <input
                        type="file"
                        className="form-control form-control-sm border-0 bg-transparent"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                      />
                      <span className="small text-muted">PDF, images, or Word</span>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm rounded-pill"
                    onClick={() => setShowUpload(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm rounded-pill px-4" disabled={uploading}>
                    {uploading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {editRecord && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(15,23,42,0.55)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <form onSubmit={handleSaveEdit}>
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold">
                    <i className="bi bi-pencil-square text-primary me-2" />
                    Edit document
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setEditRecord(null)} />
                </div>
                <div className="modal-body pt-3">
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Title</label>
                    <input
                      className="form-control form-control-sm"
                      value={editRecord.title}
                      onChange={(e) => setEditRecord({ ...editRecord, title: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Type</label>
                    <FilterSelect
                      value={editRecord.document_type}
                      onChange={(document_type) => setEditRecord({ ...editRecord, document_type })}
                      options={DOC_TYPES}
                      placeholder="Type"
                      aria-label="Document type"
                      size="sm"
                      variant="bordered"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Notes</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      value={editRecord.description || ''}
                      onChange={(e) => setEditRecord({ ...editRecord, description: e.target.value })}
                    />
                  </div>
                  <div className="mb-0">
                    <label className="form-label small fw-semibold">Replace file (optional)</label>
                    <input
                      type="file"
                      className="form-control form-control-sm"
                      onChange={(e) => setEditRecord({ ...editRecord, newFile: e.target.files[0] })}
                    />
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm rounded-pill"
                    onClick={() => setEditRecord(null)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm rounded-pill px-4" disabled={uploading}>
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserHealthRecords;
